import { useContext, useEffect, useMemo } from 'react';
import { useLatest } from './useLatest';
import type { IUnsavedEditSource, UnsavedEditArea } from '../lib/UnsavedEditArea';
import { UnsavedEditContext } from '../context/UnsavedEditContext';

/** Зарегистрировать правку в области `area`, пока компонент смонтирован. */
export const useAreaSource = (area: UnsavedEditArea | null, source: IUnsavedEditSource, editing: boolean): void => {
    const latest = useLatest(source);
    // правка-черновик: области важно, есть ли решение, — смена его наличия регистрирует правку заново
    const decides = source.beforeLeave !== undefined;
    const stable = useMemo<IUnsavedEditSource>(
        () => ({
            hasUnsavedInput: () => latest.current.hasUnsavedInput(),
            busy: () => latest.current.busy?.() ?? false,
            unsavedOnUnload: () => latest.current.unsavedOnUnload?.() ?? false,
            get question() {
                return latest.current.question;
            },
            ...(decides && { beforeLeave: () => latest.current.beforeLeave?.() ?? Promise.resolve(true) }),
        }),
        [latest, decides],
    );
    useEffect(() => area?.register(stable, editing), [area, stable, editing]);
};

/**
 * Правка страницы (карточка, свой редактор, форма настроек, документ): уход с неё — другая строка, вкладка,
 * страница, закрытие вкладки браузера — спрашивает «Закрыть без сохранения?» (или `question` правки). `editing` —
 * компонент в режиме правки: только тогда держится перехват ухода; `busy` правки защищает её и в просмотре (идёт
 * удаление). Вне `UnsavedEditScope` ничего не делает.
 */
export const useUnsavedEditSource = (source: IUnsavedEditSource, editing = true): void =>
    useAreaSource(useContext(UnsavedEditContext), source, editing);
